import React, { useState, useEffect } from 'react';

const MusicPage = () => {
  const [upcomingShows, setUpcomingShows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUpcomingShows();
  }, []);

  const fetchUpcomingShows = async () => {
    try {
      const response = await fetch('/api/shows/upcoming');
      if (response.ok) {
        const data = await response.json();
        setUpcomingShows(data);
      }
    } catch (error) {
      console.error('Error fetching upcoming shows:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatShowDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const generateDirectionsUrl = (show) => {
    const address = `${show.address}, ${show.city}, ${show.state} ${show.zip_code}`;
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Songs</h1>
          <p className="text-lg text-gray-600 italic">Craving ear candy? We've got you covered!</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          <div className="bg-gray-200 rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Sand</h3>
                <p className="text-gray-600 text-sm italic mb-4">
                  "High from this hilltop, wind swept crag<br/>
                  Clutch my hope around me like tattered rags..."
                </p>
              </div>
              <button className="text-blue-600 hover:text-blue-800">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </button>
            </div>
          </div>

          <div className="bg-gray-200 rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Otherland</h3>
                <p className="text-gray-600 text-sm italic mb-4">
                  "Sit down! You're gonna have to make this out on your own..."
                </p>
              </div>
              <button className="text-blue-600 hover:text-blue-800">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </button>
            </div>
          </div>

          <div className="bg-gray-200 rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Buckethead</h3>
                <p className="text-gray-600 text-sm italic mb-4">
                  "They strategize with their wicked minds<br/>
                  They stream their lies through my open eyes into my bucket head..."
                </p>
              </div>
              <button className="text-blue-600 hover:text-blue-800">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </button>
            </div>
          </div>

          <div className="bg-gray-200 rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Mystery</h3>
                <p className="text-gray-600 text-sm italic mb-4">
                  "I took a holiday far away from me<br/>
                  I sailed my ship out on the seas of green..."
                </p>
              </div>
              <button className="text-blue-600 hover:text-blue-800">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </button>
            </div>
          </div>

          <div className="bg-gray-200 rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Against the Grain</h3>
                <p className="text-gray-600 text-sm italic mb-4">
                  "I sing a new song but I know it goes against the grain..."
                </p>
              </div>
              <button className="text-blue-600 hover:text-blue-800">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </button>
            </div>
          </div>

          <div className="bg-gray-200 rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Time</h3>
                <p className="text-gray-600 text-sm italic mb-4">
                  "Time is drifting away an hour slips by then another<br/>
                  Time keeps turning from me leaving me behind to the end of my days..."
                </p>
              </div>
              <button className="text-blue-600 hover:text-blue-800">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Upcoming Shows</h2>
          {loading ? (
            <div className="text-center py-8">
              <div className="text-gray-500">Loading shows...</div>
            </div>
          ) : upcomingShows.length > 0 ? (
            <div className="space-y-6">
              {upcomingShows.map((show) => (
                <div key={show.id} className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{show.venue_name}</h3>
                  <p className="text-gray-600 mb-3">
                    {formatShowDate(show.date)} • {show.start_time}
                    {show.end_time ? ` - ${show.end_time}` : ''}
                  </p>
                  <div className="text-gray-700">
                    <div>{show.address}</div>
                    <div>{show.city}, {show.state} {show.zip_code}</div>
                    {show.description && (
                      <div className="mt-2 text-sm text-gray-600">{show.description}</div>
                    )}
                    <div className="mt-2">
                      {show.website_url && (
                        <a 
                          href={show.website_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 mr-4"
                        >
                          Website
                        </a>
                      )}
                      <a 
                        href={generateDirectionsUrl(show)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Directions
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-500 mb-2">No upcoming shows scheduled</div>
              <div className="text-sm text-gray-400">Check back later for future performances!</div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Sound</h2>
          <p className="text-gray-700 mb-4">
            Doulos creates fresh and unique original songs in a sea of the ordinary. Our music ranges 
            from high energy progressive rock to soul-searching introspective pieces and cinematic 
            musical experiences.
          </p>
          <p className="text-gray-700 mb-4">
            We weave influences from legendary bands like Led Zeppelin, Pink Floyd, Rush, Yes, King's X, 
            and Genesis into our alternative/progressive rock sound. In our live performances, you'll 
            also hear us cover songs from bands like Foo Fighters, Stone Temple Pilots, Collective Soul, 
            and Pink Floyd.
          </p>
          <p className="text-gray-700 font-semibold">
            Doulos... beyond the ordinary.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MusicPage;